/**
 * Navigation and summary-reopen handlers for the NCCN evaluation panel.
 * @module useNccnPanelActions
 */
import { MutableRefObject, useCallback } from "react";
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import {
  EvaluationItem,
  EvaluationResult,
  HumanVerdict,
} from "../../../data-model/nccnGuidelines";
import { MedicalNecessityAnalytics } from "../analytics/useMedicalNecessityAnalytics";
import {
  getGuidelineDisplayPositions,
  GuidelineReviewInput,
} from "../utils/nccnReviewFeedback";

/** Inputs for {@link useNccnPanelActions}. */
export interface UseNccnPanelActionsArgs {
  analytics: MedicalNecessityAnalytics;
  guidelines: NCCNEvaluationRecord[];
  currentGuideline: NCCNEvaluationRecord | undefined;
  currentItem: EvaluationItem | undefined;
  guidelineDisplayPosition: number;
  criteriaIndex: number;
  isSummaryStep: boolean;
  /** The current guideline-level review values (human verdict source). */
  currentReview: GuidelineReviewInput;
  /** Order-level AI verdict for the decision screen. */
  aiOrderVerdict: EvaluationResult | null | undefined;
  /** Order-level human verdict for the decision screen. */
  humanOrderVerdict: EvaluationResult | null | undefined;
  /** Whether the current guideline is being reviewed via the override flow. */
  isOverrideMode: boolean;
  /**
   * Whether the current guideline is not the last on the mandatory track.
   * A successful skip of the last mandatory guideline is terminal and should
   * land on the review screen.
   */
  hasNextMandatoryGuideline: boolean;
  /**
   * While in override mode, the full-array index of the next unreviewed
   * guideline in the same AI group (AI-Met, or AI-Not-Met / Undetermined) as
   * the current one, or null when this skip is terminal and should return to
   * the review screen.
   */
  nextSkipTargetIndex: number | null;
  pendingActionIdRef: MutableRefObject<string | undefined>;
  beginOverride: (index: number) => void;
  endOverride: () => void;
  goStep: (delta: number) => void;
  skipToNextGuideline: (reasoning: string) => Promise<boolean>;
  commitDraft: () => Promise<boolean>;
  submitReview: () => Promise<boolean>;
  refetch: () => Promise<void>;
  setPhase: (phase: "stepthrough" | "summary") => void;
}

/**
 * Builds Review / Override / Edit / navigate handlers with analytics side effects.
 *
 * @param args - Review state, analytics, and phase setters
 * @returns Panel action handlers
 */
export const useNccnPanelActions = ({
  analytics,
  guidelines,
  currentGuideline,
  currentItem,
  guidelineDisplayPosition,
  criteriaIndex,
  isSummaryStep,
  currentReview,
  aiOrderVerdict,
  humanOrderVerdict,
  isOverrideMode,
  hasNextMandatoryGuideline,
  nextSkipTargetIndex,
  pendingActionIdRef,
  beginOverride,
  endOverride,
  goStep,
  skipToNextGuideline,
  commitDraft,
  submitReview,
  refetch,
  setPhase,
}: UseNccnPanelActionsArgs) => {
  const handleReview = useCallback(async () => {
    const guideline = currentGuideline;
    const displayIndex = guidelineDisplayPosition;
    const saved = await submitReview();
    if (!saved) return;
    if (guideline && isSummaryStep) {
      pendingActionIdRef.current = analytics.trackVerdictReviewClicked(
        guideline,
        displayIndex,
        currentReview.verdict,
        aiOrderVerdict,
        humanOrderVerdict,
      );
      analytics.trackGuidelineCompleted(
        guideline,
        displayIndex,
        "finalized",
        currentReview.verdict,
      );
    }
    await refetch();
    setPhase("summary");
  }, [
    analytics,
    aiOrderVerdict,
    currentGuideline,
    currentReview.verdict,
    guidelineDisplayPosition,
    humanOrderVerdict,
    isSummaryStep,
    pendingActionIdRef,
    refetch,
    setPhase,
    submitReview,
  ]);

  const handleOverrideToMet = useCallback(
    (index: number) => {
      const guideline = guidelines[index];
      if (guideline) {
        const positions = getGuidelineDisplayPositions(guidelines);
        pendingActionIdRef.current = analytics.trackOverrideToMetClicked(
          guideline,
          positions[index] ?? index + 1,
        );
      }
      analytics.clearSummaryView();
      beginOverride(index);
      setPhase("stepthrough");
    },
    [analytics, beginOverride, guidelines, pendingActionIdRef, setPhase],
  );

  const handleEditReview = useCallback(
    (index: number) => {
      const guideline = guidelines[index];
      if (guideline) {
        const positions = getGuidelineDisplayPositions(guidelines);
        pendingActionIdRef.current = analytics.trackEditReviewClicked(
          guideline,
          positions[index] ?? index + 1,
        );
      }
      analytics.clearSummaryView();
      beginOverride(index);
      setPhase("stepthrough");
    },
    [analytics, beginOverride, guidelines, pendingActionIdRef, setPhase],
  );

  const handleOverrideDone = useCallback(async () => {
    const guideline = currentGuideline;
    const displayIndex = guidelineDisplayPosition;
    const saved = await submitReview();
    if (!saved) return;
    if (guideline && isSummaryStep) {
      analytics.trackGuidelineCompleted(
        guideline,
        displayIndex,
        "finalized",
        currentReview.verdict,
      );
    }
    await refetch();
    endOverride();
    setPhase("summary");
  }, [
    analytics,
    currentGuideline,
    currentReview.verdict,
    endOverride,
    guidelineDisplayPosition,
    isSummaryStep,
    refetch,
    setPhase,
    submitReview,
  ]);

  const navigate = useCallback(
    async (delta: number) => {
      const guideline = currentGuideline;
      const item = currentItem;
      const displayIndex = guidelineDisplayPosition;
      const criterionNumber = criteriaIndex + 1;
      const leavingCriterion = !isSummaryStep && !!item;
      const leavingVerdict = isSummaryStep;

      const saved = await commitDraft();
      if (!saved) return;

      if (delta < 0 && leavingCriterion && guideline && item) {
        pendingActionIdRef.current = analytics.trackBackClicked(
          guideline,
          displayIndex,
          item,
          criterionNumber,
        );
      }
      if (delta < 0 && leavingVerdict && guideline) {
        pendingActionIdRef.current = analytics.trackBackClicked(
          guideline,
          displayIndex,
        );
      }

      let nextGuidelineActionId: string | undefined;
      if (delta > 0 && leavingVerdict && guideline) {
        nextGuidelineActionId = analytics.trackVerdictNextGuidelineClicked(
          guideline,
          displayIndex,
        );
        pendingActionIdRef.current = nextGuidelineActionId;
      }

      if (leavingCriterion && guideline && item) {
        let nextActionId: string | undefined;
        if (delta > 0) {
          nextActionId = analytics.trackNextClicked(
            guideline,
            displayIndex,
            item,
            criterionNumber,
          );
          pendingActionIdRef.current = nextActionId;
        }
        analytics.trackFeedbackSubmitted(
          guideline,
          displayIndex,
          item,
          criterionNumber,
        );
        if (delta > 0) {
          analytics.trackCriterionCompleted(
            guideline,
            displayIndex,
            item,
            criterionNumber,
            nextActionId,
          );
        }
      }

      if (delta > 0 && leavingVerdict && guideline) {
        analytics.trackGuidelineCompleted(
          guideline,
          displayIndex,
          "finalized",
          currentReview.verdict,
          nextGuidelineActionId,
        );
      }

      goStep(delta);
    },
    [
      analytics,
      commitDraft,
      criteriaIndex,
      currentGuideline,
      currentItem,
      currentReview.verdict,
      goStep,
      guidelineDisplayPosition,
      isSummaryStep,
      pendingActionIdRef,
    ],
  );

  const handleSkipToNext = useCallback(
    async (reasoning: string) => {
      const guideline = currentGuideline;
      const displayIndex = guidelineDisplayPosition;
      if (guideline) {
        pendingActionIdRef.current = analytics.trackSkipGuidelineClicked(
          guideline,
          displayIndex,
        );
      }
      const saved = await skipToNextGuideline(reasoning);
      if (!saved) return false;
      if (guideline) {
        // Leave the skip action ID in the ref so the next guideline's
        // *_viewed events link back to this skip (cleared by the tracker).
        analytics.trackSkipConfirmed(
          guideline,
          displayIndex,
          pendingActionIdRef.current,
        );
        // A skip always concludes the guideline as Not Met; the in-memory
        // record may not yet reflect the persisted verdict, so pass it.
        analytics.trackGuidelineCompleted(
          guideline,
          displayIndex,
          "skipped",
          HumanVerdict.NotMet,
          pendingActionIdRef.current,
        );
      }
      if (isOverrideMode) {
        // The guideline was opened from the summary via the override flow.
        // Chain to the next unreviewed guideline in the same AI group as the
        // current one (no refetch: one would reset the override cursor via
        // the evaluation effect); once that group is exhausted, the skip
        // returns to the review screen.
        if (nextSkipTargetIndex !== null) {
          beginOverride(nextSkipTargetIndex);
        } else {
          await refetch();
          endOverride();
          setPhase("summary");
        }
      } else if (!hasNextMandatoryGuideline) {
        // Skipping the last mandatory guideline concludes the review (every
        // mandatory guideline is now Not Met), so land on the review screen.
        await refetch();
        setPhase("summary");
      }
      return true;
    },
    [
      analytics,
      currentGuideline,
      guidelineDisplayPosition,
      hasNextMandatoryGuideline,
      isOverrideMode,
      nextSkipTargetIndex,
      pendingActionIdRef,
      refetch,
      setPhase,
      skipToNextGuideline,
      beginOverride,
      endOverride,
    ],
  );

  return {
    handleReview,
    handleOverrideToMet,
    handleEditReview,
    handleOverrideDone,
    handleSkipToNext,
    navigate,
  };
};
